import React, { useState, useMemo } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ApiEndpoint, PermissionPage, PermissionPageEndpoint } from '../../services/permissions.service';
import { GrantedFilter, passesGrantedFilter } from './grantedFilter';

interface UnifiedPermissionsViewProps {
  selectedRoutes: Set<string>;
  onToggleRoute: (routePath: string) => void;
  apiEndpoints: Record<string, ApiEndpoint[]>;
  apiEndpointPermissions: Map<number, { can_read: boolean; can_write: boolean }>;
  permissionPages: PermissionPage[];
  onRemovePageAssignment: (assignmentId: number) => void;
  onToggleApiRead: (endpointId: number) => void;
  onToggleApiWrite: (endpointId: number) => void;
  onToggleApiAccess: (endpointId: number, method: string) => void;
  searchFilter: string;
  grantedFilter?: GrantedFilter;
  moduleOrder?: string[];
}

const methodColors: Record<string, string> = {
  GET: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  POST: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  PUT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  DELETE: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  PATCH: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
};

export const UnifiedPermissionsView: React.FC<UnifiedPermissionsViewProps> = ({
  selectedRoutes,
  onToggleRoute,
  apiEndpoints: _apiEndpoints,
  apiEndpointPermissions,
  permissionPages,
  onRemovePageAssignment,
  onToggleApiRead: _onToggleApiRead,
  onToggleApiWrite: _onToggleApiWrite,
  onToggleApiAccess,
  searchFilter,
  grantedFilter = 'all',
  moduleOrder = [],
}) => {
  const [collapsedPages, setCollapsedPages] = useState<Set<string>>(new Set());
  const [collapsedModules, setCollapsedModules] = useState<Set<string>>(new Set());

  const togglePage = (routePath: string) => {
    setCollapsedPages(prev => {
      const next = new Set(prev);
      if (next.has(routePath)) {
        next.delete(routePath);
      } else {
        next.add(routePath);
      }
      return next;
    });
  };

  const toggleModule = (module: string) => {
    setCollapsedModules(prev => {
      const next = new Set(prev);
      if (next.has(module)) {
        next.delete(module);
      } else {
        next.add(module);
      }
      return next;
    });
  };

  const hasAccess = (ep: PermissionPageEndpoint): boolean => {
    const perm = apiEndpointPermissions.get(ep.endpoint_id);
    if (!perm) return false;
    return ep.method === 'GET' ? perm.can_read : perm.can_write;
  };

  const grantAllAccessForEndpoints = (endpoints: PermissionPageEndpoint[]) => {
    endpoints.forEach(ep => {
      if (!hasAccess(ep)) onToggleApiAccess(ep.endpoint_id, ep.method);
    });
  };

  const revokeAllAccessForEndpoints = (endpoints: PermissionPageEndpoint[]) => {
    endpoints.forEach(ep => {
      if (hasAccess(ep)) onToggleApiAccess(ep.endpoint_id, ep.method);
    });
  };

  // Read = GET (pull) endpoints; write = POST/PUT/DELETE/PATCH. Grant only the
  // matching, not-yet-granted endpoints so these are additive (never revoke).
  const grantReadForEndpoints = (endpoints: PermissionPageEndpoint[]) => {
    endpoints.forEach(ep => {
      if (ep.method === 'GET' && !hasAccess(ep)) onToggleApiAccess(ep.endpoint_id, ep.method);
    });
  };

  const grantWriteForEndpoints = (endpoints: PermissionPageEndpoint[]) => {
    endpoints.forEach(ep => {
      if (ep.method !== 'GET' && !hasAccess(ep)) onToggleApiAccess(ep.endpoint_id, ep.method);
    });
  };

  const revokeReadForEndpoints = (endpoints: PermissionPageEndpoint[]) => {
    endpoints.forEach(ep => {
      if (ep.method === 'GET' && hasAccess(ep)) onToggleApiAccess(ep.endpoint_id, ep.method);
    });
  };

  const revokeWriteForEndpoints = (endpoints: PermissionPageEndpoint[]) => {
    endpoints.forEach(ep => {
      if (ep.method !== 'GET' && hasAccess(ep)) onToggleApiAccess(ep.endpoint_id, ep.method);
    });
  };

  // Tokenize search — every whitespace-separated token must match somewhere.
  const searchTokens = useMemo(
    () => searchFilter.toLowerCase().split(/\s+/).filter(Boolean),
    [searchFilter]
  );

  // Pre-compute search text per page and per endpoint so typing in the
  // search box stays O(visible × tokens) string-includes calls.
  type PageSearchData = {
    page: PermissionPage;
    pageSearchText: string;
    endpointSearchTexts: string[]; // index-aligned with page.endpoints
  };
  const pageSearchData: PageSearchData[] = useMemo(() => {
    return permissionPages.map(page => {
      const pageBase = [page.route_name, page.route_path, page.module]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const endpointSearchTexts = page.endpoints.map(ep =>
        [pageBase, ep.path, ep.method, ep.name].filter(Boolean).join(' ').toLowerCase()
      );
      return { page, pageSearchText: pageBase, endpointSearchTexts };
    });
  }, [permissionPages]);

  // Filter pages and trim each page's endpoint list down to entries that
  // satisfy every token. Pages with no endpoints fall back to matching
  // against page-level fields.
  type FilteredPage = {
    page: PermissionPage;
    matched: PermissionPageEndpoint[];
  };
  const filteredPages: FilteredPage[] = useMemo(() => {
    const hasSearch = searchTokens.length > 0;

    // Fast path: nothing to filter, show every page untouched.
    if (!hasSearch && grantedFilter === 'all') {
      return pageSearchData.map(({ page }) => ({ page, matched: page.endpoints }));
    }

    const result: FilteredPage[] = [];
    for (const { page, pageSearchText, endpointSearchTexts } of pageSearchData) {
      // Pages with no endpoints match only on page-level fields and have
      // nothing granted/ungranted to surface — hide them whenever the
      // granted filter is active (Hide Granted or Only Granted).
      if (page.endpoints.length === 0) {
        if (grantedFilter !== 'all') continue;
        if (!hasSearch || searchTokens.every(t => pageSearchText.includes(t))) {
          result.push({ page, matched: [] });
        }
        continue;
      }
      const visible: PermissionPageEndpoint[] = [];
      for (let i = 0; i < page.endpoints.length; i++) {
        const ep = page.endpoints[i];
        if (!passesGrantedFilter(grantedFilter, hasAccess(ep))) continue;
        if (hasSearch) {
          const text = endpointSearchTexts[i];
          let allMatch = true;
          for (const token of searchTokens) {
            if (!text.includes(token)) {
              allMatch = false;
              break;
            }
          }
          if (!allMatch) continue;
        }
        visible.push(ep);
      }
      if (visible.length > 0) result.push({ page, matched: visible });
    }
    return result;
  }, [pageSearchData, searchTokens, grantedFilter, apiEndpointPermissions]);

  // Group filtered pages by module.
  const pagesByModule = useMemo(() => {
    return filteredPages.reduce((acc, item) => {
      const key = item.page.module;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {} as Record<string, FilteredPage[]>);
  }, [filteredPages]);

  // Sort modules by the provided order, then alphabetical for the rest.
  const sortedModules = useMemo(() => {
    return Object.entries(pagesByModule).sort(([a], [b]) => {
      const ia = moduleOrder.indexOf(a);
      const ib = moduleOrder.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [pagesByModule, moduleOrder]);

  return (
    <div className="space-y-4">
      {sortedModules.map(([moduleName, pages]) => {
        const isModuleCollapsed = collapsedModules.has(moduleName);

        return (
          <div key={moduleName} className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              onClick={() => toggleModule(moduleName)}
              className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-t-lg"
            >
              <div className="flex items-center">
                {isModuleCollapsed ? (
                  <ChevronRightIcon className="h-5 w-5 mr-2 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 mr-2 text-gray-500" />
                )}
                <h3 className="text-sm font-semibold text-heading">
                  {moduleName}
                </h3>
                <span className="ml-2 text-xs text-muted">
                  ({pages.length} page{pages.length !== 1 ? 's' : ''})
                </span>
              </div>
            </button>

            {!isModuleCollapsed && (
              <div className="border-t border-gray-200 dark:border-gray-700">
                {pages.map(({ page, matched }) => {
                  const isPageCollapsed = collapsedPages.has(page.route_path);
                  const hasPageAccess = selectedRoutes.has(page.route_path);
                  const accessCount = matched.filter(ep => hasAccess(ep)).length;
                  // Read = GET endpoints, write = the rest. When every endpoint
                  // of a type is already granted, the button flips to "Remove".
                  const readEndpoints = matched.filter(ep => ep.method === 'GET');
                  const writeEndpoints = matched.filter(ep => ep.method !== 'GET');
                  const allReadGranted = readEndpoints.length > 0 && readEndpoints.every(ep => hasAccess(ep));
                  const allWriteGranted = writeEndpoints.length > 0 && writeEndpoints.every(ep => hasAccess(ep));

                  return (
                    <div
                      key={page.route_path}
                      className="border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                    >
                      <div className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <button onClick={() => togglePage(page.route_path)} className="mr-2">
                          {isPageCollapsed ? (
                            <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                          )}
                        </button>

                        <input
                          type="checkbox"
                          checked={hasPageAccess}
                          onChange={() => onToggleRoute(page.route_path)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />

                        <div className="ml-3 flex-1">
                          <p className="text-sm font-medium text-heading">
                            {page.route_name}
                          </p>
                          <p className="text-xs text-muted">
                            {page.route_path}
                          </p>
                        </div>

                        {matched.length > 0 && (
                          <div className="flex flex-wrap items-center justify-end gap-2 mr-2">
                            <span className={`text-xs ${accessCount === matched.length ? 'text-green-600 dark:text-green-400' : 'text-muted'}`}>
                              {accessCount}/{matched.length} enabled
                            </span>
                            {readEndpoints.length > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (allReadGranted) {
                                    revokeReadForEndpoints(matched);
                                  } else {
                                    grantReadForEndpoints(matched);
                                  }
                                }}
                                title={allReadGranted
                                  ? 'Revoke read on all GET (pull) endpoints for this page'
                                  : 'Grant read on all GET (pull) endpoints for this page'}
                                className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/20"
                              >
                                {allReadGranted ? 'Remove Read All' : 'Grant Read All'}
                              </button>
                            )}
                            {writeEndpoints.length > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (allWriteGranted) {
                                    revokeWriteForEndpoints(matched);
                                  } else {
                                    grantWriteForEndpoints(matched);
                                  }
                                }}
                                title={allWriteGranted
                                  ? 'Revoke write on all POST/PUT/DELETE/PATCH endpoints for this page'
                                  : 'Grant write on all POST/PUT/DELETE/PATCH endpoints for this page'}
                                className="text-xs px-2 py-1 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-600 dark:text-amber-400 dark:hover:bg-amber-900/20"
                              >
                                {allWriteGranted ? 'Remove Write All' : 'Grant Write All'}
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                grantAllAccessForEndpoints(matched);
                              }}
                              disabled={accessCount === matched.length}
                              className="text-xs px-2 py-1 rounded border border-green-300 text-green-700 hover:bg-green-50 dark:border-green-600 dark:text-green-400 dark:hover:bg-green-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Enable All
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                revokeAllAccessForEndpoints(matched);
                              }}
                              disabled={accessCount === 0}
                              className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Disable All
                            </button>
                          </div>
                        )}
                      </div>

                      {!isPageCollapsed && matched.length > 0 && (
                        <div className="bg-gray-50 dark:bg-gray-800/50 pl-12 pr-3 py-2 space-y-1">
                          <div className="text-xs font-medium text-muted mb-2">
                            Required API Endpoints:
                          </div>
                          {matched.map(ep => {
                            const granted = hasAccess(ep);
                            return (
                              <div key={ep.endpoint_id} className="flex items-center gap-2 py-1">
                                <input
                                  type="checkbox"
                                  checked={granted}
                                  onChange={() => onToggleApiAccess(ep.endpoint_id, ep.method)}
                                  className="h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${methodColors[ep.method] || 'bg-gray-100 text-gray-800'}`}>
                                  {ep.method}
                                </span>
                                <span className="text-xs text-label flex-1">
                                  {ep.path}
                                </span>
                                {ep.required && (
                                  <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                                    Required
                                  </span>
                                )}
                                {ep.is_shared && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 font-medium">
                                    Shared
                                  </span>
                                )}
                                <button
                                  onClick={() => onRemovePageAssignment(ep.assignment_id)}
                                  title="Remove this endpoint from this page"
                                  className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                                >
                                  <XMarkIcon className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {!isPageCollapsed && matched.length === 0 && (
                        <div className="bg-gray-50 dark:bg-gray-800/50 pl-12 pr-3 py-2">
                          <p className="text-xs text-muted italic">
                            No API endpoints assigned to this page
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
